import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead, createEvent, createFamilyMember, createActivity, fetchFamilyMembers, fetchActivities } from '../lib/api';
import { 
  Home, Users, FileText, 
  Bell, Plus, LogOut, X, Check,
  Calendar as CalendarIcon, UserPlus, Dumbbell,
  Zap, Settings, BarChart3
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: Home },
  { path: '/family', label: 'My Family', icon: Users },
  { path: '/statistics', label: 'Stats', icon: BarChart3 },
  { path: '/smartdaddy', label: 'SmartDaddy AI', icon: Zap },
];

const ACTIVITY_COLORS = [
  { value: 'hsl(142, 71%, 45%)', name: 'Green' },
  { value: 'hsl(24, 95%, 53%)', name: 'Orange' },
  { value: 'hsl(199, 89%, 48%)', name: 'Blue' },
  { value: 'hsl(328, 85%, 60%)', name: 'Pink' },
  { value: 'hsl(271, 81%, 56%)', name: 'Purple' },
  { value: 'hsl(84, 81%, 44%)', name: 'Lime' },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [fabOpen, setFabOpen] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Sports background images
  const sportsImages = [
    'https://images.unsplash.com/photo-1622659097509-4d56de14539e?w=1920&q=80', // Kids playing soccer
    'https://images.unsplash.com/photo-1574744918163-6cef6f4a31b0?w=1920&q=80', // Girl swimming
    'https://images.unsplash.com/photo-1490326149782-dd42fa59bd9f?w=1920&q=80', // Little league baseball
    'https://images.unsplash.com/photo-1576610616656-d3aa5d1f4534?w=1920&q=80', // Kids swimming lesson
    'https://images.unsplash.com/photo-1600077063877-22118d6290eb?w=1920&q=80', // Kids soccer group
  ];
  
  // Dialog states
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showChildDialog, setShowChildDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  
  // Form states
  const [eventForm, setEventForm] = useState({
    title: '', child_id: '', activity_id: '', date: new Date(),
    start_time: '09:00', end_time: '10:00', location: ''
  });
  const [childForm, setChildForm] = useState({ name: '', color: ACTIVITY_COLORS[0].value });
  const [activityForm, setActivityForm] = useState({ name: '', color: ACTIVITY_COLORS[0].value });

  // Auto-rotate background slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sportsImages.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [sportsImages.length]);

  useEffect(() => {
    loadNotifications();
    loadFormData();
  }, []);

  const loadNotifications = async () => {
    try {
      const data = await fetchNotifications();
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const loadFormData = async () => {
    try {
      const [members, acts] = await Promise.all([
        fetchFamilyMembers(),
        fetchActivities()
      ]);
      setFamilyMembers(members);
      setActivities(acts);
    } catch (error) {
      console.error('Failed to load form data:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title) {
      toast.error('Please enter an event title');
      return;
    }
    try {
      await createEvent({
        ...eventForm,
        date: format(eventForm.date, 'yyyy-MM-dd'),
        source: 'manual'
      });
      toast.success('Event created!');
      setShowEventDialog(false);
      setEventForm({
        title: '', child_id: '', activity_id: '', date: new Date(),
        start_time: '09:00', end_time: '10:00', location: ''
      });
      loadNotifications();
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleCreateChild = async () => {
    if (!childForm.name) {
      toast.error('Please enter a name');
      return;
    }
    try {
      const created = await createFamilyMember(childForm);
      setFamilyMembers(prev => [...prev, created]);
      toast.success('Child added!');
      setShowChildDialog(false);
      setChildForm({ name: '', color: ACTIVITY_COLORS[0].value });
    } catch (error) {
      toast.error('Failed to add child');
    }
  };

  const handleCreateActivity = async () => {
    if (!activityForm.name) {
      toast.error('Please enter an activity name');
      return;
    }
    try {
      const created = await createActivity(activityForm);
      setActivities(prev => [...prev, created]);
      toast.success('Activity created!');
      setShowActivityDialog(false);
      setActivityForm({ name: '', color: ACTIVITY_COLORS[0].value });
    } catch (error) {
      toast.error('Failed to create activity');
    }
  };

  const openFabAction = (action) => {
    setFabOpen(false);
    if (action === 'event') setShowEventDialog(true);
    if (action === 'child') setShowChildDialog(true);
    if (action === 'activity') setShowActivityDialog(true);
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex flex-col">
      {/* Dynamic Sports Background */}
      <div className="fixed inset-0 z-0">
        {sportsImages.map((img, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{
              opacity: currentSlide === index ? 1 : 0,
              backgroundImage: `url('${img}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        ))}
        {/* Light overlay for readability */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.88) 50%, rgba(255,255,255,0.92) 100%)',
          }}
        />
        {/* Subtle gradient accent */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(93,173,226,0.05) 0%, rgba(142,68,173,0.05) 100%)',
          }}
        />
      </div>

      {/* Desktop Header - Apple Intelligence Style */}
      <header className="hidden md:block sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-gray-100/50">
        <div className="w-full px-4 md:px-8 lg:px-12 xl:px-16">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_kidplay-hub/artifacts/7zuwi818_jgzjhpdo_image.png"
                alt="SmartDaddy AI"
                className="h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Nav - Floating Pill Style */}
            <nav 
              className="flex items-center gap-1 p-1.5 rounded-full"
              style={{
                background: 'rgba(0,0,0,0.04)',
              }}
            >
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    location.pathname === path
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {label}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="header-bell-button">
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 flex items-center justify-center text-[10px] font-bold text-white bg-[hsl(var(--destructive))] rounded-full px-1">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="font-semibold">Notifications</span>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs">
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-muted-foreground text-sm">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.slice(0, 10).map(notification => (
                        <DropdownMenuItem
                          key={notification.id}
                          className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${!notification.read ? 'bg-[hsl(var(--primary)/0.05)]' : ''}`}
                          data-testid="notif-item"
                        >
                          <span className="font-medium text-sm">{notification.title}</span>
                          <span className="text-xs text-muted-foreground">{notification.message}</span>
                        </DropdownMenuItem>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] flex items-center justify-center text-white text-sm font-semibold">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-3 py-2">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* BETA Badge */}
              <span className="px-2.5 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-[10px] font-bold rounded-full shadow-sm">
                BETA
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header - Apple Style */}
      <header className="md:hidden sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-gray-100/50">
        <div className="flex h-14 items-center justify-between px-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_kidplay-hub/artifacts/7zuwi818_jgzjhpdo_image.png"
              alt="SmartDaddy AI"
              className="h-7 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative rounded-full h-9 w-9" data-testid="header-bell-button">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 flex items-center justify-center text-[9px] font-bold text-white bg-[hsl(var(--destructive))] rounded-full px-1">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="font-semibold text-sm">Notifications</span>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs h-7">
                      Mark all read
                    </Button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No notifications
                    </div>
                  ) : (
                    notifications.slice(0, 5).map(notification => (
                      <DropdownMenuItem
                        key={notification.id}
                        className={`flex flex-col items-start gap-0.5 p-3 ${!notification.read ? 'bg-[hsl(var(--primary)/0.05)]' : ''}`}
                        data-testid="notif-item"
                      >
                        <span className="font-medium text-sm">{notification.title}</span>
                        <span className="text-xs text-muted-foreground">{notification.message}</span>
                      </DropdownMenuItem>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full h-9 w-9">
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] flex items-center justify-center text-white text-xs font-semibold">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-3 py-2">
                  <p className="font-medium text-sm">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')} className="text-sm">
                  <Settings className="mr-2 h-4 w-4" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="text-destructive text-sm">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* BETA Badge - Mobile */}
            <span className="px-2 py-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-[9px] font-bold rounded-full">
              BETA
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 w-full px-4 md:px-8 lg:px-12 xl:px-16 py-4 md:py-6 safe-bottom md:pb-6 flex-1">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="bottom-nav md:hidden">
        <div className="flex justify-around items-center py-1">
          {navItems.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              className={`bottom-nav-item ${location.pathname === path ? 'active' : ''}`}
            >
              <div className="nav-icon">
                <Icon className="h-5 w-5" />
              </div>
              <span>{label}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* FAB with Menu */}
      <div className="fab-container">
        {fabOpen && (
          <div className="fab-menu animate-fade-up">
            <button
              onClick={() => openFabAction('event')}
              className="fab-menu-item"
              data-testid="fab-add-event"
            >
              <CalendarIcon className="h-5 w-5 text-[hsl(var(--primary))]" />
              Add Event
            </button>
            <button
              onClick={() => openFabAction('child')}
              className="fab-menu-item"
              data-testid="fab-add-child"
            >
              <UserPlus className="h-5 w-5 text-[hsl(var(--secondary))]" />
              Add Child
            </button>
            <button
              onClick={() => openFabAction('activity')}
              className="fab-menu-item"
              data-testid="fab-add-activity"
            >
              <Dumbbell className="h-5 w-5 text-[hsl(var(--sport-gymnastics))]" />
              Add Activity
            </button>
          </div>
        )}
        <button
          onClick={() => setFabOpen(!fabOpen)}
          className={`fab-primary ${fabOpen ? 'rotate-45' : ''} transition-transform`}
          data-testid="quick-add-event-fab"
        >
          {fabOpen ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
        </button>
      </div>

      {/* Add Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Event</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Event Title *</Label>
              <Input
                placeholder="e.g., Soccer Practice"
                data-testid="event-title-input"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Child</Label>
                <Select
                  value={eventForm.child_id}
                  onValueChange={(v) => setEventForm({ ...eventForm, child_id: v })}
                >
                  <SelectTrigger data-testid="event-child-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {familyMembers.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Activity</Label>
                <Select
                  value={eventForm.activity_id}
                  onValueChange={(v) => setEventForm({ ...eventForm, activity_id: v })}
                >
                  <SelectTrigger data-testid="event-activity-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {activities.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start" data-testid="event-date-open">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(eventForm.date, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={eventForm.date}
                    onSelect={(d) => d && setEventForm({ ...eventForm, date: d })}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Start</Label>
                <Input
                  type="time"
                  value={eventForm.start_time}
                  onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>End</Label>
                <Input
                  type="time"
                  value={eventForm.end_time}
                  onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Location</Label>
              <Input
                placeholder="e.g., Riverside Park"
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateEvent} data-testid="event-submit-button">
              <Check className="mr-2 h-4 w-4" /> Add Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Child Dialog */}
      <Dialog open={showChildDialog} onOpenChange={setShowChildDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Child</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name *</Label>
              <Input
                placeholder="Child's name"
                data-testid="family-member-name-input"
                value={childForm.name}
                onChange={(e) => setChildForm({ ...childForm, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {ACTIVITY_COLORS.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    className={`h-8 w-8 rounded-full transition-transform hover:scale-110 ${childForm.color === c.value ? 'ring-2 ring-offset-2 ring-[hsl(var(--ring))]' : ''}`}
                    style={{ backgroundColor: c.value }}
                    onClick={() => setChildForm({ ...childForm, color: c.value })}
                    data-testid="child-color-swatch"
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChildDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateChild} data-testid="save-family-member-button">
              <Check className="mr-2 h-4 w-4" /> Add Child
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Activity Dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Activity</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Activity Name *</Label>
              <Input
                placeholder="e.g., Soccer, Dance, Swimming"
                data-testid="activity-name-input"
                value={activityForm.name}
                onChange={(e) => setActivityForm({ ...activityForm, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {ACTIVITY_COLORS.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    className={`h-8 w-8 rounded-full transition-transform hover:scale-110 ${activityForm.color === c.value ? 'ring-2 ring-offset-2 ring-[hsl(var(--ring))]' : ''}`}
                    style={{ backgroundColor: c.value }}
                    onClick={() => setActivityForm({ ...activityForm, color: c.value })}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivityDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateActivity} data-testid="save-activity-button">
              <Check className="mr-2 h-4 w-4" /> Add Activity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Desktop Footer */}
      <footer className="hidden md:block relative z-10 border-t border-gray-100/50 bg-white/70 backdrop-blur-sm mt-auto">
        <div className="w-full px-4 md:px-8 lg:px-12 xl:px-16 py-4 flex flex-wrap justify-between items-center">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} SmartDaddy AI. All rights reserved. | An{' '}
            <a href="https://offworldventures.org" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
              OffWorld Ventures
            </a>{' '}
            company.
          </p>
          <div className="flex gap-6 text-xs text-gray-400">
            <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms of Use</Link>
            <Link to="/contact" className="hover:text-gray-600 transition-colors">Contact Us</Link>
            <Link to="/pricing" className="hover:text-gray-600 transition-colors">Pricing</Link>
          </div>
        </div>
      </footer>

      {/* Mobile Footer - shown above bottom nav */}
      <footer className="md:hidden relative z-10 border-t border-gray-100/50 bg-white/70 backdrop-blur-sm mb-16">
        <div className="px-4 py-3 text-center">
          <div className="flex justify-center gap-4 text-xs text-gray-400 mb-2">
            <Link to="/privacy" className="hover:text-gray-600">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-600">Terms</Link>
            <Link to="/contact" className="hover:text-gray-600">Contact</Link>
            <Link to="/pricing" className="hover:text-gray-600">Pricing</Link>
          </div>
          <p className="text-[10px] text-gray-400">
            © {new Date().getFullYear()} SmartDaddy AI | An{' '}
            <a href="https://offworldventures.org" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
              OffWorld Ventures
            </a>{' '}
            company
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
